import React, { useEffect, useState } from 'react';
import {
  AppDatePicker,
  AppMultiSelect,
  AppRemixIcon,
  AppTextArea,
  AppModal,
  AppNoInfo,
} from '@/layout/AppComponents';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import Moment from 'moment';
import * as Yup from 'yup';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import Translator from '../../../../../../components/helpers/Translator';
import * as currentPageAction from '../../../../../../../store/actions/currentPageAction';
import { axiosPath } from '../../../../../../../routing/helpers/router';
import minions from '../../../../../../../minions/minions';
import EvidenceNewForm from '../Evidence/EvidenceNewForm';
import errorHandler from '@/layout/components/error/ErrorHandler';

const CalendarNewEventForm = (props) => {
  const { plantObligationID, handleAddCalendarDate, isExternal } = props;
  const { moduleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const permissions = useSelector((s) => s.module[moduleBridgeHex].permissions);
  const poLevel3 = useSelector(
    (s) => s.currentPage?.plantObligationLevels?.level3
  );
  const evidences = useSelector(
    (s) => s.currentPage?.evidenceTabs?.[plantObligationID]
  );
  const [showCreateEvidenceModal, setShowCreateEvidenceModal] = useState(false);
  const [isCreatingEvidence, setIsCreatingEvidence] = useState(false);

  const addCalendarValidationSchema = Yup.object().shape({
    date: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)
    ),
  });

  // > LOAD EVIDENCE TAB
  const loadEvidenceTab = async () => {
    try {
      const response = await axios.post(
        axiosPath("api_r_user_module_legalCompliance_evidences_tab"),
        {
          moduleBridgeHex,
          plantHex,
          plantObligationID,
        }
      );
      return response.data;
    } catch (err) {}
  };
  useEffect(() => {
    if (evidences === undefined && !isExternal) {
      loadEvidenceTab().then((r) => {
        dispatch(
          currentPageAction.change('evidenceTabs', { [plantObligationID]: r })
        );
      });
    }
  }, []);
  // < LOAD EVIDENCE TAB

  // < ADD EVIDENCE
  const handleAddEvidence = async (evidenceIDs, eventValues, setFieldValue) => {
    if (isCreatingEvidence) return;
    setIsCreatingEvidence(true);
    try {
      const response = await axios.post(
        axiosPath(
          "api_r_user_module_legalCompliance_calendar_createAttachEvidence"
        ),
        minions.forms.toFormData({
          ...eventValues,
          plantHex,
          plantObligationID,
        }),
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      dispatch(
        currentPageAction.change('evidenceTabs', {
          [plantObligationID]: response.data.evidences,
        })
      );
      if (permissions?.gcl?.module?.plant?.states?.needEvidenceToFulfilled) {
        const updatedLevel3 = { ...poLevel3 };
        updatedLevel3[plantObligationID].alerts.hasEvidences =
          response.data.hasEvidences;

        if (
          permissions?.gcl?.module?.plant?.states?.needEvidenceToFulfilled
            ?.needFile
        ) {
          updatedLevel3[plantObligationID].alerts.hasEvidencesWithFiles =
            response.data.hasEvidencesWithFiles;
        }

        dispatch(
          currentPageAction.change('plantObligationLevels', {
            level3: updatedLevel3,
          })
        );
      }
      const updatedEvidenceIDs = [...evidenceIDs];
      updatedEvidenceIDs.push(response.data.createdEvidenceID);
      setFieldValue('evidenceIDs', updatedEvidenceIDs);
      setIsCreatingEvidence(false);
      setShowCreateEvidenceModal(false);
    } catch (err) {
      setIsCreatingEvidence(false);
      setShowCreateEvidenceModal(false);
      dispatch(errorHandler(err));
    }
  };
  // < ADD EVIDENCE

  if (evidences === undefined && !isExternal)
    return <AppNoInfo minHeight={300} />;

  return (
    <Formik
      enableReinitialize
      initialValues={{
        date: Moment().format('DD/MM/YYYY'),
        comment: '',
        evidenceIDs: [],
      }}
      validationSchema={addCalendarValidationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(v) => handleAddCalendarDate(v)}
    >
      {({ handleSubmit, values, setFieldValue, isSubmitting }) => (
        <div className="p-2">
          {!isExternal && (
            <h3>
              <Translator file="module-legalCompliance" name="add.new.event" />
            </h3>
          )}
          <div className="row-no-margin">
            <div className="row">
              <div className={`${isExternal ? 'col-lg-12' : 'col-lg-6 pl-0'}`}>
                <AppDatePicker
                  title={ReactDOMServer.renderToString(
                    <Translator file="module-legalCompliance" name="date" />
                  )}
                  value={values.date}
                  onChange={(opt) => setFieldValue('date', opt)}
                  popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                  showMonthDropdown
                  showYearDropdown
                />
              </div>
            </div>
          </div>
          {!isExternal && permissions?.gcl?.module?.plant?.evidences && (
            <div className="d-flex flex-row">
              <AppMultiSelect
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-legalCompliance"
                    name="attached.evidence"
                  />
                )}
                icon={<AppRemixIcon icon="apps-2" />}
                value={values.evidenceIDs}
                options={evidences}
                onChange={(v) => setFieldValue('evidenceIDs', v)}
                setLabelField="selectName"
                setValueField="id"
                isLoading={!evidences}
                hasSearchBox
                hasSelectOptions
                allowSelectAll
              />
              <CreateLinkBtn
                className="btn btn-sm btn-success"
                onClick={() => setShowCreateEvidenceModal(true)}
              >
                <Translator
                  file="module-legalCompliance"
                  name="create.and.link"
                />
              </CreateLinkBtn>
            </div>
          )}
          <AppTextArea
            title={ReactDOMServer.renderToString(
              <Translator file="module-legalCompliance" name="comment" />
            )}
            placeholder={ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name="optional.comment"
              />
            )}
            icon={<AppRemixIcon icon="align-left" noVariants />}
            onChange={(opt) => setFieldValue('comment', opt)}
            value={values.comment}
          />
          <span
            className={`btn btn-primary float-right mt-2 ${
              isSubmitting ? 'disabled' : ''
            }`}
            onClick={handleSubmit}
          >
            <Translator file="module-legalCompliance" name="create.event" />
          </span>
          <AppModal
            title={ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name="create.evidence"
              />
            )}
            show={showCreateEvidenceModal}
            size="lg"
            onClose={() => setShowCreateEvidenceModal(false)}
          >
            <EvidenceNewForm
              handleAddEvidence={(v) =>
                handleAddEvidence(values.evidenceIDs, v, setFieldValue)
              }
            />
          </AppModal>
        </div>
      )}
    </Formik>
  );
};
export default CalendarNewEventForm;

const CreateLinkBtn = styled.span`
  margin: 10px 0 10px 10px !important;
  line-height: 24px;
`;
